
import { useState, useEffect, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ChevronLeft, ChevronRight, Eye, Trash2, CheckCircle, XCircle, Send, Copy, Square, CheckSquare, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { PageHeader, Button, Pill } from "@/components/ui";
import { cn } from "@/lib/utils";
import { adminApi } from "@/lib/adminApi";

interface Proposal {
  id: string;
  title: string;
  proposeeName: string;
  status: string;
  createdAt: string;
  user: { id: string; name: string | null; email: string };
}

const STATUS_OPTIONS = ["ALL", "DRAFT", "PUBLISHED", "SENT", "ACCEPTED", "DECLINED"];
const BULK_STATUS_OPTIONS = ["DRAFT", "PUBLISHED", "SENT", "ACCEPTED", "DECLINED"];

const statusColors: Record<string, string> = {
  DRAFT: "bg-zinc-100",
  PUBLISHED: "bg-emerald-100",
  SENT: "bg-blue-100 dark:bg-blue-900/50",
  ACCEPTED: "bg-purple-100",
  DECLINED: "bg-red-100 dark:bg-red-900/50",
};

export default function ProposalsPage() {
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("ALL");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchTimeout, setSearchTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkStatus, setBulkStatus] = useState("PUBLISHED");

  const fetchProposals = async (pageNum = 1) => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.get<{
        success: boolean;
        proposals: Proposal[];
        total: number;
        pages: number;
      }>(`/admin/proposals?page=${pageNum}&search=${encodeURIComponent(search)}&status=${status}`);
      if (res.success) {
        setProposals(res.proposals);
        setTotal(res.total);
        setTotalPages(res.pages);
        setPage(pageNum);
      }
    } catch (e: any) {
      setError(e.message || "Failed to load proposals");
      setProposals([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProposals(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const handleSearchChange = (val: string) => {
    setSearch(val);
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => fetchProposals(1), 400);
    setSearchTimeout(timeout);
  };

  const handleStatusChange = (newStatus: string) => {
    setStatus(newStatus);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    fetchProposals(newPage);
  };

  const handleUpdateStatus = async (proposalId: string, newStatus: string) => {
    setActionLoading(proposalId);
    try {
      await adminApi.put(`/admin/proposals/${proposalId}`, { status: newStatus });
      fetchProposals(page);
    } catch (e) {
      console.error("Failed to update proposal", e);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSend = async (proposalId: string) => {
    if (!confirm("Send this proposal to the proposee?")) return;
    setActionLoading(proposalId);
    try {
      await adminApi.put(`/admin/proposals/${proposalId}/send`, {});
      fetchProposals(page);
    } catch (e) {
      console.error("Failed to send proposal", e);
      alert("Failed to send proposal. It may not be in PUBLISHED status.");
    } finally {
      setActionLoading(null);
    }
  };

  const handleAccept = async (proposalId: string) => {
    if (!confirm("Manually accept this proposal?")) return;
    setActionLoading(proposalId);
    try {
      await adminApi.put(`/admin/proposals/${proposalId}/accept`, {});
      fetchProposals(page);
    } catch (e) {
      console.error("Failed to accept proposal", e);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDecline = async (proposalId: string) => {
    if (!confirm("Manually decline this proposal?")) return;
    setActionLoading(proposalId);
    try {
      await adminApi.put(`/admin/proposals/${proposalId}/decline`, {});
      fetchProposals(page);
    } catch (e) {
      console.error("Failed to decline proposal", e);
    } finally {
      setActionLoading(null);
    }
  };

  const handleClone = async (proposalId: string) => {
    setActionLoading(proposalId);
    try {
      await adminApi.post(`/admin/proposals/${proposalId}/clone`, {});
      fetchProposals(page);
    } catch (e) {
      console.error("Failed to clone proposal", e);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (proposalId: string) => {
    if (!confirm("Delete this proposal? This cannot be undone.")) return;
    try {
      await adminApi.delete(`/admin/proposals/${proposalId}`);
      fetchProposals(page);
    } catch (e) {
      console.error("Failed to delete proposal", e);
    }
  };

  const handleViewDetails = (proposalId: string) => {
    navigate(`/proposals/${proposalId}`);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === proposals.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(proposals.map(p => p.id)));
    }
  };

  const toggleSelectOne = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkUpdate = async () => {
    if (selectedIds.size === 0) return;
    setActionLoading('bulk');
    try {
      await adminApi.put('/admin/proposals/bulk', {
        proposalIds: Array.from(selectedIds),
        status: bulkStatus,
      });
      setSelectedIds(new Set());
      setShowBulkModal(false);
      fetchProposals(page);
    } catch (e) {
      console.error("Failed to bulk update", e);
      alert("Failed to bulk update proposals");
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="WORKSPACE"
        title="Proposals"
        subtitle={
          loading
            ? "Loading…"
            : `${total.toLocaleString()} ${total === 1 ? "proposal" : "proposals"} across the platform.`
        }
        actions={
          selectedIds.size > 0 ? (
            <Button variant="primary" onClick={() => setShowBulkModal(true)}>
              <CheckSquare className="mr-2 h-4 w-4" />
              Bulk Update ({selectedIds.size})
            </Button>
          ) : null
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
          <input
            type="text"
            placeholder="Search by title or proposee…"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="h-10 w-64 rounded-lg border border-zinc-200 bg-white pl-10 pr-4 text-sm placeholder:text-zinc-400 focus:border-merry-pink focus:outline-none focus:ring-1 focus:ring-merry-pink dark:border-zinc-700 dark:bg-zinc-900 dark:placeholder:text-zinc-500"
          />
        </div>
        <div className="inline-flex items-center gap-1 rounded-xl border border-zinc-200 bg-white p-1 dark:border-zinc-700 dark:bg-zinc-900">
          {STATUS_OPTIONS.map((s) => (
            <Pill
              key={s}
              tone={status === s ? 'accent' : 'muted'}
              onClick={() => handleStatusChange(s)}
            >
              {s}
            </Pill>
          ))}
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent" />
            </div>
          ) : error ? (
            <div className="py-12 text-center">
              <p className="text-sm">{error}</p>
              <button
                onClick={() => fetchProposals(page)}
                className="mt-2 text-xs hover:underline"
              >
                Retry
              </button>
            </div>
          ) : proposals.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-sm">No proposals found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="px-4 py-3 text-left w-12">
                      <button
                        onClick={toggleSelectAll}
                        className="rounded p-1 hover:bg-zinc-200"
                        title="Select all"
                      >
                        {selectedIds.size === proposals.length ? (
                          <CheckSquare className="h-4 w-4" />
                        ) : (
                          <Square className="h-4 w-4" />
                        )}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide">
                      Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide">
                      Creator
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wide">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
                  {proposals.map((proposal) => (
                    <Fragment key={proposal.id}>
                      <tr className="hover:bg-zinc-50/50">
                        <td className="px-4 py-4">
                          <button
                            onClick={() => toggleSelectOne(proposal.id)}
                            className="rounded p-1 hover:bg-zinc-200"
                          >
                            {selectedIds.has(proposal.id) ? (
                              <CheckSquare className="h-4 w-4" />
                            ) : (
                              <Square className="h-4 w-4" />
                            )}
                          </button>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <p className="text-sm font-medium">
                              {proposal.title}
                            </p>
                            <p className="text-xs">
                              For: {proposal.proposeeName}
                            </p>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {proposal.user.name || proposal.user.email}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={cn(
                              "inline-flex rounded-full px-2 py-0.5 text-xs font-medium",
                              statusColors[proposal.status] || statusColors.DRAFT
                            )}
                          >
                            {proposal.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {new Date(proposal.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => handleViewDetails(proposal.id)}
                              className="flex items-center gap-1 rounded-lg p-1.5"
                              title="View details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            {proposal.status === "PUBLISHED" && (
                              <button
                                onClick={() => handleSend(proposal.id)}
                                disabled={actionLoading === proposal.id}
                                className="flex items-center gap-1 rounded-lg p-1.5 hover:bg-blue-50 disabled:opacity-50 dark:hover:bg-blue-900/50"
                                title="Send proposal"
                              >
                                <Send className="h-4 w-4" />
                              </button>
                            )}
                            {(proposal.status === "SENT" || proposal.status === "PUBLISHED") && (
                              <>
                                <button
                                  onClick={() => handleAccept(proposal.id)}
                                  disabled={actionLoading === proposal.id}
                                  className="flex items-center gap-1 rounded-lg p-1.5 text-green-600 hover:bg-green-50 disabled:opacity-50 dark:text-green-400 dark:hover:bg-green-900/50"
                                  title="Accept proposal"
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleDecline(proposal.id)}
                                  disabled={actionLoading === proposal.id}
                                  className="flex items-center gap-1 rounded-lg p-1.5 hover:bg-red-50 disabled:opacity-50 dark:hover:bg-red-900/50"
                                  title="Decline proposal"
                                >
                                  <XCircle className="h-4 w-4" />
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => handleClone(proposal.id)}
                              disabled={actionLoading === proposal.id}
                              className="flex items-center gap-1 rounded-lg p-1.5 disabled:opacity-50"
                              title="Clone proposal"
                            >
                              <Copy className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(proposal.id)}
                              className="flex items-center gap-1 rounded-lg p-1.5 hover:bg-red-50 dark:hover:bg-red-900/50"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {loading && !error && proposals.length > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm">
            Page {page} of {totalPages} — {total.toLocaleString()} proposals
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={page <= 1}
              className="flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ChevronLeft className="h-4 w-4" />
              Prev
            </button>
            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= totalPages}
              className="flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Bulk Update Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-bold">Bulk Update Proposals</h2>
              <button
                onClick={() => setShowBulkModal(false)}
                className="rounded-lg p-1"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-4">
              <p className="text-sm">
                Update {selectedIds.size} selected proposals to:
              </p>
              <div>
                <label className="block text-sm font-medium">New Status</label>
                <select
                  value={bulkStatus}
                  onChange={(e) => setBulkStatus(e.target.value)}
                  className="mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm"
                >
                  {BULK_STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                onClick={() => setShowBulkModal(false)}
                className="rounded-lg border px-4 py-2 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkUpdate}
                disabled={!!actionLoading}
                className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {actionLoading === 'bulk' ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                ) : null}
                Update {selectedIds.size} Proposals
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}